import fs from 'node:fs/promises'
import path from 'node:path'
import { cancel, confirm, isCancel, text } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'

import galaxy from '@scalar/galaxy/3.1.json'
import { slugify } from '@scalar-org/helpers/url'
import {
  type ScalarConfig,
  scalarConfigSchema,
} from '@scalar-org/scalar-config'

import { quickstartMarkdown } from '@/domains/project/init/quickstart'
import { output } from '@/helpers/std-out'

import {
  CONFIG_FILE_DEFAULT,
  DEFAULT_MARKDOWN_FILE_NAME,
  DEFAULT_OPENAPI_FILE_NAME,
  findConfig,
  getCurrentFolderName,
} from '../helpers'

async function fileExists(path: string) {
  try {
    await fs.access(path, fs.constants.F_OK)
    return true
  } catch {
    return false
  }
}

export const InitCommand = () => {
  const cmd = new Command('init')

  cmd.description('Create a new Scalar Docs project.')
  cmd.option('-s, --subdomain [url]', 'subdomain to publish on')
  cmd.option('--force', 'override existing configuration')
  cmd.action(async ({ subdomain, force }) => {
    // Path to `scalar.config.json` fileF
    const configFile = findConfig() || CONFIG_FILE_DEFAULT
    let validInput: boolean

    const nextSteps = () => {
      output
        .info()
        .line('What to do next:')
        .line(
          `  ${as.cyan('scalar project preview')} to preview your project locally`,
        )
        .line(
          `  ${as.cyan('scalar project check-config')} to validate your scalar.config.json file`,
        )
        .line(
          `  ${as.cyan('scalar project publish --slug')} ${as.gray('[dashboard-project-slug]')} to publish your project`,
        )
        .line(
          as.white(
            `Run ${as.magenta('scalar --help')} to see all available commands.`,
          ),
        )
        .print()
    }

    // Handle cancel from the user
    const handleCancel = () => {
      cancel('Operation cancelled.')
      nextSteps()
      process.exit(0)
    }

    // Check if `scalar.config.json` already exists
    if (await fileExists(configFile)) {
      output
        .info()
        .line(
          `${as.green('⚠')} Found existing configuration: ${as.green.bold(`${configFile}`)}`,
        )
        .print()

      if (force) {
        output
          .info()
          .line(`${as.green('✔')} Overwriting existing file…`)
          .print()
      }

      const shouldOverwriteExisting =
        force ??
        (await confirm({
          message: 'Do you want to override the file?',
          initialValue: false,
        }))

      if (isCancel(shouldOverwriteExisting)) {
        handleCancel()
      }

      if (!shouldOverwriteExisting) {
        handleCancel()
      }
    }

    const currentDir = path.resolve(path.dirname(configFile))

    // New configuration object
    const configuration: ScalarConfig = scalarConfigSchema.parse({
      info: {},
      navigation: {
        routes: {
          '/': {
            type: 'group',
            title: 'Documentation',
            children: {
              '/': {
                type: 'page',
                title: 'Quickstart',
                filepath: DEFAULT_MARKDOWN_FILE_NAME,
              },
              '/openapi': {
                type: 'openapi',
                title: 'Scalar Galaxy',
                mode: 'flat',
                filepath: DEFAULT_OPENAPI_FILE_NAME,
              },
            },
          },
        },
      },
    })

    // Subdomain
    validInput = !!subdomain

    const folderName = getCurrentFolderName()

    while (!validInput) {
      const response = await text({
        message: 'What is the name of your project?',
        defaultValue: folderName,
        placeholder: folderName,
      })

      // TODO: Check if the subdomain is available
      if (isCancel(response)) {
        handleCancel()
      } else {
        validInput = true
      }
      const title = response.toString()
      const slug = slugify(title)

      configuration.info = { title, description: `Documentation for ${title}` }
      subdomain = slug

      output
        .info()
        .line(`${as.green('✔')} Subdomain: ${as.green(subdomain)}`)
        .print()
    }

    configuration.siteConfig.subdomain = subdomain.trim()

    // Create `scalar.config.json` file and write it on the filesystem
    const content = JSON.stringify(configuration, null, 2)

    // Starter Scalar config
    await fs.writeFile(configFile, content)

    // Starter markdown file
    await fs.writeFile(
      path.join(currentDir, DEFAULT_MARKDOWN_FILE_NAME),
      quickstartMarkdown,
    )

    // Starter OpenAPI file
    await fs.writeFile(
      path.join(currentDir, DEFAULT_OPENAPI_FILE_NAME),
      JSON.stringify(galaxy, null, 2),
    )

    output
      .info()
      .line(`${as.green('✔')} Configuration stored. \n`)
      .line(`${as.green.bold(`${configFile}`)} \n`)
      .line(
        `${as.grey(
          content
            .split('\n')
            .map((line) => `  ${line}`)
            .join('\n'),
        )}`,
      )
      .print()
    nextSteps()
  })

  return cmd
}
